import { DeliveryOrderStatusE } from "../../models/enums";

/**
 * spec-26 Phase 2 — the ONE frontend definition of "this delivery-service order has been paid
 * for, so do not put the customer in front of a payment form again".
 *
 * It mirrors the STATUS arm of the backend's `PaidOrderGuard.IsPaid`
 * (`JumboMedia.BL/SubManagers/Payment/PaidOrderGuard.cs`). The backend also trusts
 * `PaymentVerifiedAt`, which no view model exposes, so the two are not identical — the backend
 * is strictly the more inclusive of the two and remains the authority. That asymmetry is safe in
 * this direction only: a "not paid" answer here opens the payment form, and the server then
 * refuses the second charge. An answer that wrongly said "paid" would strand a customer who
 * genuinely still owes us money, which is why the fallback below is `false`.
 *
 * 🔴 `Pending Verification` and `Verification Failed` are deliberately NOT paid. The backend
 * parks an order there with `PaymentVerifiedAt` left NULL on purpose
 * (`DeliveryServiceFulfilmentPort.RecordUnverified`), so claiming "paid" would contradict it.
 * Those customers are still never shown the paypage again — `Payment.tsx` catches them with
 * `isFinalising` and its own wording, because "we cannot confirm your charge yet" is a different
 * thing to say to someone than "you have paid". Do not fold them in here without moving that too.
 */
export const isDeliveryOrderPaid = (
  status?: DeliveryOrderStatusE | null
): boolean =>
  status === DeliveryOrderStatusE.Billed ||
  status === DeliveryOrderStatusE["Invoice Issued"];
